import React from 'react';
import { Radar } from 'react-chartjs-2';
import { RatingIcon } from '@apps/config/icons.config';
import ArrowRightIcon from '@mui/icons-material/ArrowForwardOutlined';
import StarIcon from '@mui/icons-material/Star';
import { Box, Button, Rating, Stack, Typography } from '@mui/material';
import { BorderTitle } from '@stack/ui/components';
import {
  Chart,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  ChartTypeRegistry,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import { DistributiveArray } from 'chart.js/dist/types/utils';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import mean from 'lodash/mean';

import { Props } from './props';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const Ratings: React.FC<Props> = (props) => {
  const {
    data,
    showTitle = true,
    moreDetail = false,
    onClickMoreDetail,
  } = props;
  const { rating, totalUserRated, disappointment, satisfaction } = data || {};
  const isEmptyData = isEmpty(rating);

  const totalRating = !isEmptyData ? mean(rating).toFixed(1) : '0.0';
  const isEmptySubContent =
    isEmpty(satisfaction) && isEmpty(disappointment) && !moreDetail;

  const radarGraph = () => {
    const data: ChartData<'radar', number[], string> = {
      labels: [
        'Exterior Design',
        'Power',
        'Space',
        'Interior Design',
        'Safety',
        'Cost',
      ],
      datasets: [
        {
          label: 'Car Ratings',
          data: rating,
          // backgroundColor: 'rgba(255,225,118,0.8)',
          backgroundColor: 'rgba(168, 181, 201, 0.3)',
          // borderColor: '#FFC800',
          borderColor: 'rgb(168, 181, 201)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          hitRadius: 0,
        },
      ],
    };

    const options: ChartOptions<'radar'> = {
      aspectRatio: 230 / 260,
      devicePixelRatio: 4,
      scales: {
        r: {
          angleLines: {
            display: true,
            lineWidth: 1,
            color: '#A8B5C9CC',
          },
          grid: {
            display: false,
          },
          suggestedMin: 0,
          suggestedMax: 5,
          ticks: {
            display: false,
          },
          pointLabels: {
            padding: (ctx) => {
              switch (ctx.index) {
                case 0:
                  return 28;
                case 1:
                case 2:
                case 4:
                case 5:
                  return 12;
                case 3:
                  return 8;
              }
            },
            font: {
              family: 'Roboto',
              size: 14,
              weight: 500,
            },
            color: '#67708C',
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
    };

    const centerTextPlugin = {
      id: 'centerText',
      afterDraw: (
        chart: Chart<
          'radar',
          DistributiveArray<ChartTypeRegistry['radar']['defaultDataPoint']>,
          unknown
        >
      ) => {
        const ctx = chart.ctx;
        const x = chart.width / 2;
        const y = chart.height / 1.85;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = 'bold 36px Roboto';
        ctx.fillStyle = '#1E242F';
        ctx.fillText(totalRating, x, y);
        ctx.restore();
      },
    };

    const labelValuePlugin = {
      id: 'labelValue',
      afterDraw: (
        chart: Chart<
          'radar',
          DistributiveArray<ChartTypeRegistry['radar']['defaultDataPoint']>,
          unknown
        >
      ) => {
        const scale = chart.scales.r;
        const ctx: CanvasRenderingContext2D = scale.ctx;
        get(scale, '_pointLabelItems', [])?.forEach((item, index: number) => {
          const { x, y } = item;
          const finalX =
            index === 0 || index === 3 ? x : x < 50 ? x - 20 : x * 1.1;
          ctx.save();
          ctx.translate(finalX, y);
          ctx.textAlign = 'center';
          ctx.font = 'bold 14px Roboto';
          ctx.fillStyle = '#1E242F';
          ctx.fillText(
            !isEmptyData ? rating?.[index]?.toFixed(1) : '0.0',
            0,
            30
          );
          ctx.restore();
        });
      },
      beforeDraw: (
        chart: Chart<
          'radar',
          DistributiveArray<ChartTypeRegistry['radar']['defaultDataPoint']>,
          unknown
        >
      ) => {
        const scale = chart.scales.r;
        const ctx = scale.ctx;

        const rotateAndDrawSquare = (
          x: number,
          y: number,
          offsetX: number,
          offsetY: number,
          angle: number
        ) => {
          const rotatedX =
            offsetX * Math.cos(angle) - offsetY * Math.sin(angle);
          const rotatedY =
            offsetX * Math.sin(angle) + offsetY * Math.cos(angle);
          ctx.save();
          ctx.translate(x + rotatedX, y + rotatedY);
          ctx.rotate(angle);
          ctx.fillStyle = '#A8B5C9CC';
          ctx.fillRect(-2.5, -2.5, 3, 3);
          ctx.restore();
        };

        get(scale, '_pointLabelItems', []).forEach((item, index: number) => {
          const { x, y } = item;
          switch (index) {
            case 0:
              rotateAndDrawSquare(x, y, 40, 25, 45);
              break;
            case 1:
              rotateAndDrawSquare(x, y, 5, -25, 60);
              break;
            case 2:
              rotateAndDrawSquare(x, y, -7.5, -10, 75);
              break;
            case 3:
              rotateAndDrawSquare(x, y, -6.8, -4, 45);
              break;
            case 4:
              rotateAndDrawSquare(x, y, -7, 10, 60);
              break;
            case 5:
              rotateAndDrawSquare(x, y, 2, 26, 75);
              break;
            default:
              rotateAndDrawSquare(x, y, -5, 35, 90);
              break;
          }
        });
      },
    };

    ChartJS.register(centerTextPlugin);
    ChartJS.register(labelValuePlugin);

    return (
      <Stack
        sx={{
          mt: 3,
          px: 1,
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: {
            xs: 260,
          },
        }}
      >
        <Radar data={data} options={options} />
      </Stack>
    );
  };

  const renderContent = (
    icon: React.JSX.Element,
    title: string,
    content?: string[]
  ) => {
    if (isEmpty(content)) return null;
    return (
      <Stack gap={3}>
        <Stack gap="7px" direction="row" alignItems={'center'}>
          <Box width={16} height={16}>
            {icon}
          </Box>
          <Typography fontSize={14} fontWeight={700} mt={0.5}>
            {title}
          </Typography>
        </Stack>
        <Stack gap={3} flexDirection="row" flexWrap="wrap">
          {content?.map((e, index) => (
            <Stack
              bgcolor={'#EDECED'}
              padding={'4px 8px'}
              borderRadius={0.4}
              key={index}
            >
              <Typography fontSize={12} fontWeight={500}>
                {e}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  };

  const renderSubContent = () => {
    if (isEmptySubContent) return null;
    if (moreDetail) {
      return (
        <Button
          sx={{
            fontSize: 16,
            textTransform: 'capitalizes',
            fontWeight: 700,
            '&:hover': {
              backgroundColor: 'transparent !important',
            },
            border: '1px solid #EDECED',
            padding: 5,
            borderRadius: 0,
          }}
          endIcon={<ArrowRightIcon sx={{ color: '#1E242F', ml: -1 }} />}
          color="info"
          onClick={() => onClickMoreDetail?.()}
        >
          More Details
        </Button>
      );
    }
    if (!showTitle) return null;
    return (
      <Stack
        padding={5}
        gap={5}
        justifyContent={'center'}
        border={'1px solid #EDECED'}
        sx={{ borderTop: '0px' }}
      >
        {renderContent(RatingIcon.satisfaction, 'Satisfaction', satisfaction)}
        {renderContent(
          RatingIcon.disappointment,
          'Disappointment',
          disappointment
        )}
      </Stack>
    );
  };

  if (isEmptyData) return null;
  return (
    <Stack rowGap={5}>
      {showTitle && <BorderTitle title={'Ratings'} />}

      <Stack>
        <Stack
          padding={5}
          justifyContent={'center'}
          border={'1px solid #EDECED'}
          alignItems={'center'}
          {...(moreDetail && !isEmptySubContent && { borderBottom: 'none' })}
        >
          <Stack direction="row" gap={4} alignItems={'center'}>
            <Typography style={{ color: '#1E242F', fontWeight: 700 }}>
              <span
                style={{
                  fontSize: 40,
                  fontWeight: 700,
                }}
              >
                {totalRating}{' '}
              </span>
              / 5
            </Typography>
            <Stack>
              <Rating
                name="read-only"
                size="small"
                precision={0.1}
                value={Number(totalRating)}
                sx={{ p: 0, m: 0 }}
                emptyIcon={
                  <StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />
                }
                readOnly
              />
              <Typography fontSize={14} color={'#67708C'} fontWeight={500}>
                {totalUserRated} User Ratings
              </Typography>
            </Stack>
          </Stack>
          {radarGraph()}
        </Stack>
        {renderSubContent()}
      </Stack>
    </Stack>
  );
};

export default Ratings;
